import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { anonimizarCliente } from '../api/clientes';
import { mensagemDeErro, MSG_ERRO_REDE } from '../lib/erros';
import { ApiError } from '../lib/http';
import { Drawer } from './Drawer';
import { useToast } from './toast';
import { AreaTexto, BlocoAviso, Botao } from './ui';

/**
 * Anonimização de cliente (LGPD Art. 18, SPEC §8/§10). Mesmo espírito do
 * `DrawerCancelarOS`: motivo obrigatório, ação irreversível, aviso fixo antes
 * de confirmar. Só visível pro `admin` — quem monta a tela decide isso.
 */
export function DrawerAnonimizarCliente({
  clienteId,
  clienteNome,
  aberto,
  aoFechar,
}: {
  clienteId: string;
  clienteNome: string;
  aberto: boolean;
  aoFechar: () => void;
}): ReactNode {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  const confirmar = async (): Promise<void> => {
    if (!motivo.trim()) {
      setErro('Informe o motivo da anonimização.');
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      await anonimizarCliente(clienteId, motivo.trim());
      toast('Dados do cliente anonimizados.');
      await qc.invalidateQueries({ queryKey: ['cliente', clienteId] });
      setMotivo('');
      aoFechar();
    } catch (err) {
      setErro(err instanceof ApiError && !err.ehRede ? mensagemDeErro(err.codigo) : MSG_ERRO_REDE);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Drawer aberto={aberto} titulo={`Anonimizar dados de ${clienteNome}`} aoFechar={aoFechar}>
      {erro && <BlocoAviso tom="erro">{erro}</BlocoAviso>}
      <BlocoAviso tom="aviso">
        Isso apaga nome, telefone, documento e e-mail deste cliente e desativa o código de
        indicação. Não pode ser desfeito. Indicações, vouchers e o histórico de OS continuam
        existindo, sem o dado pessoal.
      </BlocoAviso>
      <AreaTexto
        rotulo="Motivo da anonimização"
        obrigatorio
        placeholder="Ex.: solicitação do titular via LGPD Art. 18."
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
      />
      <div className="flex gap-2">
        <Botao variante="secundario" onClick={aoFechar}>
          Voltar
        </Botao>
        <Botao variante="perigo" carregando={enviando} onClick={() => void confirmar()}>
          Anonimizar dados
        </Botao>
      </div>
    </Drawer>
  );
}
